// Factual drift for the selected read (truth set, part 4; backend
// app/services/truth_set/drift.py): the share of answers that state at least
// one fact contradicting the brand's confirmed truth set. Sits under the
// headline tiles beside mention, top five and primary.
//
// Shown with its 95% margin. Compared only with the previous read checked
// against the SAME truth-set version: across versions it shows the version
// marker instead of a delta, because a fact that changed is not drift.
// Clients see percentages; an admin's hover adds the counts.

import { ShieldAlert } from 'lucide-react';

import type { ReadTruthCheck } from '@/api/deep-signal-api';
import { getModelLabel } from '@/app/constants/models';

function pct(share: number | null | undefined, digits = 1): string {
  return share == null ? 'Not measured' : `${(share * 100).toFixed(digits)}%`;
}

export function DriftStrip({ check, isAdmin, onOpenGaps }: {
  check: ReadTruthCheck | null | undefined;
  isAdmin: boolean;
  onOpenGaps: () => void;
}) {
  if (!check) return null;
  const overall = check.drift.overall;
  const comparison = check.comparison;
  const lanes = Object.entries(check.drift.by_lane);
  const countTitle = (cell: { present?: number; answers?: number }) =>
    isAdmin && cell.answers != null ? `${cell.present} of ${cell.answers} answers` : undefined;

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, padding: '10px 14px', marginTop: 12,
      border: '1px solid var(--border)', borderRadius: 10, backgroundColor: 'var(--background)',
    }}>
      <ShieldAlert className="w-4 h-4" style={{ color: 'var(--infrared-coral)' }} />
      <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }} title={countTitle(overall)}>
        Factual drift {pct(overall.share)}
      </span>
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
        of answers state a fact that contradicts your confirmed facts
        {overall.margin != null ? ` (±${(overall.margin * 100).toFixed(1)} pts)` : ''}
        {check.version_number ? ` · truth set v${check.version_number}` : ''}
      </span>
      {comparison && (
        comparison.comparable ? (
          <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600,
                         color: !comparison.moved ? 'var(--muted-foreground)'
                           : (comparison.delta ?? 0) < 0 ? 'var(--signal-green, green)' : 'var(--infrared-coral)' }}
                title={`Moves under ${((comparison.band ?? 0) * 100).toFixed(1)} pts are within the band`}>
            {comparison.moved
              ? `${(comparison.delta ?? 0) > 0 ? '+' : ''}${((comparison.delta ?? 0) * 100).toFixed(1)} pts vs ${comparison.previous_label}`
              : `No real change vs ${comparison.previous_label}`}
          </span>
        ) : (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
            {comparison.marker}: not compared with {comparison.previous_label}
          </span>
        )
      )}
      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
        {lanes.map(([lane, cell]) => (
          <span key={lane} title={countTitle(cell)} style={{
            fontSize: 'var(--text-xs)', padding: '1px 8px', borderRadius: 999, border: '1px solid var(--border)',
            color: check.drift.blend_lanes.includes(lane) ? 'var(--foreground)' : 'var(--muted-foreground)',
          }}>
            {getModelLabel(lane)} {pct(cell.share, 0)}
          </span>
        ))}
      </span>
      {check.gaps.length > 0 && (
        <button type="button" onClick={onOpenGaps} style={{
          marginLeft: 'auto', fontSize: 'var(--text-xs)', fontWeight: 600, background: 'none', border: 'none',
          color: 'var(--spectral-violet)', cursor: 'pointer',
        }}>
          {check.gaps.length} flagged gap{check.gaps.length === 1 ? '' : 's'} →
        </button>
      )}
    </div>
  );
}
